import {
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Space, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';

import {
  createAnswerOptionSetAsAdmin,
  deleteAnswerOptionSetAsAdmin,
  fetchAnswerOptionSetsAsAdmin,
  updateAnswerOptionSetAsAdmin,
} from '@/apis';
import { AnswerOptionSetModal } from '@/components/Admin/Modal';
import PageHeader from '@/components/Admin/PageHeader';
import StateRenderer from '@/components/Admin/StateRenderer';
import TableContainer from '@/components/Admin/TableContainer';
import { MessageType, OptionSource } from '@/enums';
import { useAppFeedback } from '@/hooks';
import {
  AdminAnswerOptionSet,
  CreateAnswerOptionSetAsAdminRequest,
  UpdateAnswerOptionSetAsAdminRequest,
} from '@/types/apis/admin';
import { getAnswerOptionSetLabel } from '@/utils/adminLabels';
import { formatDateTime } from '@/utils/dateFormatter';

const { Text } = Typography;

interface AnswerOptionSetWithOptions extends Omit<
  AdminAnswerOptionSet,
  'createdAt' | 'updatedAt' | 'options'
> {
  options: {
    id?: string;
    value: string;
    label: string;
    order?: number;
  }[];
}

const AdminAnswerOptionSetsPage = () => {
  const { showMessage, showConfirm } = useAppFeedback();
  const [answerOptionSets, setAnswerOptionSets] = useState<
    AdminAnswerOptionSet[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isAnswerOptionSetModalOpen, setAnswerOptionSetModalOpen] =
    useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [deletingAnswerOptionSet, setDeletingAnswerOptionSet] = useState<
    string | null
  >(null);
  const [selectedAnswerOptionSet, setSelectedAnswerOptionSet] =
    useState<AdminAnswerOptionSet | null>(null);
  const pageSize = 10;

  const loadAnswerOptionSets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchAnswerOptionSetsAsAdmin(page, pageSize);
      setAnswerOptionSets(response.data.items);
      setTotal(response.data.total);
    } catch (err) {
      const errorMessage = '載入答案選項集失敗，請稍後再試';
      setError(errorMessage);
      showMessage(errorMessage, MessageType.Error);
      console.error('Error loading answer option sets:', err);
    } finally {
      setLoading(false);
    }
  }, [page, showMessage]);

  useEffect(() => {
    loadAnswerOptionSets();
  }, [loadAnswerOptionSets]);

  const handleSaveAnswerOptionSet = async (
    data: AnswerOptionSetWithOptions,
  ) => {
    try {
      if (modalMode === 'edit' && selectedAnswerOptionSet) {
        // Edit mode
        const updateData: UpdateAnswerOptionSetAsAdminRequest = {
          name: data.name,
          description: data.description || '',
          optionSource: data.optionSource,
          options: data.options.map((opt) => ({
            id: opt.id,
            value: opt.value,
            label: opt.label,
            order: opt.order,
          })),
        };
        await updateAnswerOptionSetAsAdmin(
          selectedAnswerOptionSet.id,
          updateData,
        );
        showMessage('答案選項集更新成功', MessageType.Success);
      } else {
        // Add mode
        const createData: CreateAnswerOptionSetAsAdminRequest = {
          name: data.name,
          description: data.description || '',
          optionSource: data.optionSource,
          ...(data.optionSource === OptionSource.Static && data.options
            ? { options: data.options }
            : {}),
        };
        await createAnswerOptionSetAsAdmin(createData);
        showMessage('答案選項集新增成功', MessageType.Success);
      }
      setAnswerOptionSetModalOpen(false);
      setSelectedAnswerOptionSet(null);
      loadAnswerOptionSets();
    } catch (err) {
      const errorMessage =
        modalMode === 'edit'
          ? '答案選項集更新失敗，請稍後再試'
          : '答案選項集新增失敗，請稍後再試';
      showMessage(errorMessage, MessageType.Error);
      console.error('Error saving answer option set:', err);
    }
  };

  const handleDeleteConfirm = (answerOptionSet: AdminAnswerOptionSet) => {
    showConfirm(
      '確認刪除',
      `確定要刪除答案選項集「${answerOptionSet.name}」嗎？此操作無法復原。`,
      async () => {
        try {
          setDeletingAnswerOptionSet(answerOptionSet.id);
          await deleteAnswerOptionSetAsAdmin(answerOptionSet.id);
          showMessage('答案選項集刪除成功', MessageType.Success);
          loadAnswerOptionSets();
        } catch (err) {
          showMessage('刪除答案選項集失敗，請稍後再試', MessageType.Error);
          console.error('Error deleting answer option set:', err);
        } finally {
          setDeletingAnswerOptionSet(null);
        }
      },
      undefined,
      '確定刪除',
      '取消',
      'danger',
    );
  };

  const handleEditAnswerOptionSet = (answerOptionSet: AdminAnswerOptionSet) => {
    setSelectedAnswerOptionSet(answerOptionSet);
    setModalMode('edit');
    setAnswerOptionSetModalOpen(true);
  };

  const handleAddAnswerOptionSet = () => {
    setSelectedAnswerOptionSet(null);
    setModalMode('add');
    setAnswerOptionSetModalOpen(true);
  };

  const getOptionSourceLabel = (optionSource: OptionSource) => {
    switch (optionSource) {
      case OptionSource.Static:
        return '靜態選項';
      case OptionSource.Industry:
        return '行業動態';
      default:
        return optionSource;
    }
  };

  const getOptionSourceColor = (optionSource: OptionSource) => {
    switch (optionSource) {
      case OptionSource.Static:
        return 'blue';
      case OptionSource.Industry:
        return 'green';
      default:
        return 'default';
    }
  };

  const columns: ColumnsType<AdminAnswerOptionSet> = [
    {
      title: '名稱',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <Space>
          <Text strong>{getAnswerOptionSetLabel(name)}</Text>
          <Text type="secondary">{name}</Text>
        </Space>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: {
        showTitle: true,
      },
      render: (description: string) => (
        <Text type="secondary">{description || '-'}</Text>
      ),
    },
    {
      title: '選項來源',
      dataIndex: 'optionSource',
      key: 'optionSource',
      width: 120,
      render: (optionSource: OptionSource) => (
        <Tag color={getOptionSourceColor(optionSource)}>
          {getOptionSourceLabel(optionSource)}
        </Tag>
      ),
    },
    {
      title: '選項數量',
      key: 'optionsCount',
      width: 120,
      render: (_, item) => <Text>{item.options?.length || 0} 個選項</Text>,
    },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (createdAt: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatDateTime(new Date(createdAt))}
        </Text>
      ),
    },
    {
      title: '更新時間',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (updatedAt: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatDateTime(new Date(updatedAt))}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, item) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditAnswerOptionSet(item)}
            title="編輯答案選項集"
            size="small"
          />
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteConfirm(item)}
            loading={deletingAnswerOptionSet === item.id}
            title="刪除答案選項集"
            danger
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="答案選項集管理"
        description="管理答案選項集，包含靜態選項和動態選項。"
      />

      <TableContainer
        title="答案選項集列表"
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddAnswerOptionSet}
          >
            新增答案選項集
          </Button>
        }
      >
        <StateRenderer
          loading={loading}
          error={error}
          empty={answerOptionSets.length === 0}
          emptyMessage="開始建立您的第一個答案選項集"
          emptyIcon={<FileOutlined />}
          onRetry={loadAnswerOptionSets}
        >
          <Table
            scroll={{ x: 'max-content' }}
            columns={columns}
            dataSource={answerOptionSets}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: pageSize,
              total: total,
              onChange: setPage,
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 筆，共 ${total} 筆`,
            }}
            size="small"
            loading={loading}
          />
        </StateRenderer>
      </TableContainer>

      <AnswerOptionSetModal
        isOpen={isAnswerOptionSetModalOpen}
        answerOptionSet={selectedAnswerOptionSet}
        mode={modalMode}
        onClose={() => {
          setAnswerOptionSetModalOpen(false);
          setSelectedAnswerOptionSet(null);
        }}
        onSave={handleSaveAnswerOptionSet}
      />
    </>
  );
};

export default AdminAnswerOptionSetsPage;
